import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import NavBar from './NavBar';
import SideBar from './SideBar';

/**
 * Admin shell — same contract as the client app's shell (frontend/src/components/Menu).
 *
 * The Volt template shipped a Bootstrap-collapse sidebar: below 992px it was
 * display:none (no animation), and `.content` still reserved 95px from 768px up
 * for a sidebar that wasn't there — tablets lost width and wide tables pushed the
 * page sideways. The shell is now React-driven:
 *
 *   < 1200px  off-canvas drawer that slides in over a backdrop; the content area
 *             gets the FULL viewport width (margin-left: 0).
 *   >= 1200px sidebar is always docked, and can be contracted to an icon rail to
 *             hand ~190px back to the content (persisted in localStorage).
 *
 * Visibility is driven by classes on <body> so plain CSS can animate everything
 * (see public/css/goldhub-theme.css → "RESPONSIVE SHELL").
 */

const RAIL_KEY = 'goldhub_nav_rail';

function Menu() {
    const location = useLocation();
    const [open, setOpen] = useState(false);
    const [rail, setRail] = useState(() => {
        // No stored choice yet: start as an icon rail on smaller laptops (<1440px),
        // where a docked 260px sidebar leaves the dashboard cramped, and expanded on
        // big screens. An explicit toggle is remembered from then on.
        try {
            const stored = localStorage.getItem(RAIL_KEY);
            if (stored !== null) return stored === 'true';
        } catch (e) { /* private mode */ }
        return typeof window !== 'undefined' && window.innerWidth < 1440;
    });

    const close = useCallback(() => setOpen(false), []);

    // Drawer state lives on <body> so the sidebar, backdrop and content margins
    // can all react in CSS without prop-drilling.
    useEffect(() => {
        document.body.classList.toggle('gh-nav-open', open);
        // Lock background scrolling while the drawer covers the page.
        document.body.classList.toggle('gh-nav-locked', open);
        return () => {
            document.body.classList.remove('gh-nav-open');
            document.body.classList.remove('gh-nav-locked');
        };
    }, [open]);

    // Persist only a DELIBERATE toggle. Writing the auto default on mount froze the
    // first screen size seen (a visit on a 27" monitor kept 13" laptops expanded
    // and cramped forever).
    const railTouched = useRef(false);
    useEffect(() => {
        document.body.classList.toggle('gh-nav-rail', rail);
        if (!railTouched.current) return;
        try { localStorage.setItem(RAIL_KEY, String(rail)); } catch (e) { /* private mode */ }
    }, [rail]);

    // Any navigation closes the drawer (menu item, deep link, back button).
    useEffect(() => { setOpen(false); }, [location.pathname]);

    // Esc closes the drawer — expected for any overlay.
    useEffect(() => {
        function onKey(e) { if (e.key === 'Escape') setOpen(false); }
        document.addEventListener('keydown', onKey);
        return () => document.removeEventListener('keydown', onKey);
    }, []);

    // Clicking a real nav link inside the drawer closes it; the submenu openers
    // (data-bs-toggle="collapse") must keep the drawer open so their children
    // can be reached.
    function onSidebarClick(event) {
        const link = event.target.closest && event.target.closest('a.nav-link, .nav-link[href]');
        if (link && !link.getAttribute('data-bs-toggle')) setOpen(false);
    }

    return (
        <React.Fragment>
            <NavBar onToggleNav={() => setOpen(v => !v)} navOpen={open} />

            {/* Dock/undock the sidebar on large screens (icon rail ⇄ full width). */}
            <button
                type="button"
                className="gh-rail-toggle"
                onClick={() => { railTouched.current = true; setRail(v => !v); }}
                aria-label={rail ? 'Expand menu' : 'Collapse menu'}
                title={rail ? 'Expand menu' : 'Collapse menu'}
            >
                <svg width="16" height="16" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                    {rail
                        ? <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                        : <path fillRule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clipRule="evenodd" />}
                </svg>
            </button>

            {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events */}
            <div onClick={onSidebarClick}>
                <SideBar onClose={close} />
            </div>

            {open ? (
                <div
                    className="gh-nav-backdrop"
                    onClick={close}
                    role="presentation"
                    aria-hidden="true"
                />
            ) : null}
        </React.Fragment>
    );
}

export default Menu;
